/**
 * Subscription — tenant-facing view of current plan + invoices + upgrade.
 *
 * Tenant super_admins see:
 *   - Current plan + unlocked features
 *   - DB-backed plan catalog (read-only pricing, set by platform owner)
 *   - "Subscribe" → checkout → gateway flow (creates invoice tagged with their tenant)
 *   - Their own invoices only
 *   - Mark-paid for their own manual bank-transfer invoices
 *
 * NO cross-tenant visibility. Pricing editing lives in /plans-admin (platform owner only).
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, CreditCard, Smartphone, Landmark, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Subscription() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";

  const [plans, setPlans] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [defaultGw, setDefaultGw] = useState("manual");
  const [myPlan, setMyPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedGw, setSelectedGw] = useState("manual");
  const [currency, setCurrency] = useState("inr");
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [p, me, inv] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/plan-features"),
        isAdmin ? api.get("/billing/invoices") : Promise.resolve({ data: { invoices: [] } }),
      ]);
      setPlans(p.data.plans || []);
      setGateways(p.data.available_gateways || []);
      setDefaultGw(p.data.default_gateway || "manual");
      setSelectedGw(p.data.default_gateway || "manual");
      setMyPlan(me.data);
      setInvoices(inv.data?.invoices || []);
    } catch (e) { toast.error("Could not load subscription"); }
    finally { setLoading(false); }
  };

  const loadRazorpaySDK = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const launchRazorpay = async (orderData, plan_code) => {
    const ok = await loadRazorpaySDK();
    if (!ok) { toast.error("Could not load Razorpay. Check your network."); return; }
    const rzp = new window.Razorpay({
      key: orderData.key_id,
      amount: orderData.amount,
      currency: (orderData.currency || "inr").toUpperCase(),
      name: user?.tenant_name || "QrewHR",
      description: `Subscription · ${plan_code}`,
      order_id: orderData.order_id,
      prefill: {
        name: user?.full_name || "",
        email: user?.email || "",
      },
      theme: { color: "#7c3aed" },
      handler: async (resp) => {
        try {
          await api.post("/billing/verify", {
            gateway: "razorpay",
            order_id: resp.razorpay_order_id,
            payment_id: resp.razorpay_payment_id,
            signature: resp.razorpay_signature,
          });
          toast.success("Payment successful — plan activated!");
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Verification failed");
        }
      },
      modal: { ondismiss: () => toast.info("Checkout closed") },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(`Payment failed: ${resp.error?.description || "unknown"}`);
    });
    rzp.open();
  };

  const checkout = async (plan_code) => {
    if (!isAdmin) return toast.error("Only company super admin can subscribe");
    setCheckoutBusy(plan_code);
    try {
      const { data } = await api.post("/billing/checkout", { plan_code, currency, gateway: selectedGw });
      if (data.mock) {
        toast.success(`Mock checkout (${data.gateway}) — auto-verifying`);
        await api.post("/billing/verify", { gateway: data.gateway, order_id: data.order_id, payment_id: "mock_pay" });
        toast.success("Plan activated!");
        await load();
      } else if (data.gateway === "razorpay" && data.key_id && data.order_id) {
        await launchRazorpay(data, plan_code);
      } else if (data.checkout_url) {
        window.open(data.checkout_url, "_blank");
      } else if (data.bank_details) {
        toast.success("Bank-transfer invoice created. See Invoices below.");
        await load();
      } else {
        toast.info(`Order created: ${data.order_id}. Complete via gateway SDK.`);
        await load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
    finally { setCheckoutBusy(null); }
  };

  const markPaid = async (inv_id) => {
    try { await api.post(`/billing/mark-paid/${inv_id}`); toast.success("Invoice marked paid"); await load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading subscription…</div>;

  const gwIcon = { razorpay: <CreditCard className="w-4 h-4" />, phonepe: <Smartphone className="w-4 h-4" />, stripe: <CreditCard className="w-4 h-4" />, manual: <Landmark className="w-4 h-4" /> };
  const isMock = (gwId, list) => {
    const g = (list || []).find(x => x.id === gwId);
    return !g || g.mock !== false;
  };

  // Always show all 4 billing cycles so users can discover Quarterly/Half-yearly even if
  // platform owner hasn't created a plan for that cycle yet.
  const ALL_CYCLES = ["monthly", "quarterly", "half_yearly", "annual"];
  const cycleOptions = ALL_CYCLES;
  const visiblePlans = plans.filter((p) => p.billing_cycle === cycle || p.billing_cycle === "trial");

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Your Company</div>
        <h1 className="text-2xl font-bold font-[Outfit]">Subscription</h1>
        <p className="text-sm text-slate-500 mt-1">Your current plan and billing history. Pricing is set by the platform and shown read-only.</p>
      </div>

      <Card data-testid="my-plan-card">
        <CardHeader><CardTitle className="text-base font-[Outfit]">Current plan</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <Badge className="text-sm uppercase tracking-wider bg-cyan-600" data-testid="my-plan-badge">{myPlan?.plan_name || myPlan?.plan}</Badge>
            <span className="text-xs text-slate-500">Seats: <span className="font-mono text-slate-900">{myPlan?.seat_limit}</span></span>
            <span className="text-xs text-slate-500">Cycle: <span className="font-mono text-slate-900 capitalize">{(myPlan?.billing_cycle || "—").replace("_", " ")}</span></span>
            <span className="text-xs text-slate-500">Status: <span className="font-mono text-slate-900">{myPlan?.status}</span></span>
            {myPlan?.trial_ends_at && <span className="text-xs text-amber-600">Trial ends: {new Date(myPlan.trial_ends_at).toLocaleDateString()}</span>}
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Unlocked features ({myPlan?.feature_keys?.length || 0})</div>
            <div className="flex flex-wrap gap-1">
              {(myPlan?.feature_keys || []).map((f) => <span key={f} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">{f}</span>)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============== INTEGRATIONS ============== */}
      {isAdmin && (
        <Card data-testid="integrations-card">
          <CardHeader>
            <CardTitle className="text-base font-[Outfit] flex items-center justify-between">
              Payment Integrations
              <span className="text-[10px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wider">{gateways.length} available</span>
            </CardTitle>
            <p className="text-[12px] text-slate-500 mt-1">Connect a gateway to accept UPI, cards, netbanking and international payments. Status reflects keys present in your backend env.</p>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { id: "razorpay", label: "Razorpay",  desc: "UPI · Cards · Netbanking · India", region: "IN" },
                { id: "phonepe",  label: "PhonePe",   desc: "UPI · Wallet · India",            region: "IN" },
                { id: "stripe",   label: "Stripe",    desc: "Cards · International",            region: "GLOBAL" },
                { id: "paypal",   label: "PayPal",    desc: "International payouts",            region: "GLOBAL" },
                { id: "manual",   label: "Bank Transfer", desc: "Manual NEFT / RTGS / SWIFT",   region: "ALL" },
              ].map((p) => {
                const matched = gateways.find(g => g.id === p.id);
                const enabled = !!matched;
                const isMock = matched?.mock !== false;
                return (
                  <div
                    key={p.id}
                    className={`p-4 rounded-xl border transition ${selectedGw === p.id ? "border-violet-300 bg-violet-50/40 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}
                    data-testid={`integration-${p.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {gwIcon[p.id] || <CreditCard className="w-4 h-4 text-slate-500" />}
                        <span className="font-bold font-[Outfit] text-slate-900">{p.label}</span>
                      </div>
                      {enabled ? (
                        isMock
                          ? <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Test mode</span>
                          : <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wider">Soon</span>
                      )}
                    </div>
                    <div className="text-[12px] text-slate-600 mb-3">{p.desc}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{p.region}</span>
                      {enabled ? (
                        <button
                          onClick={() => setSelectedGw(p.id)}
                          className={`text-[12px] font-semibold ${selectedGw === p.id ? "text-violet-700" : "text-slate-700 hover:text-violet-700"}`}
                          data-testid={`integration-select-${p.id}`}
                        >
                          {selectedGw === p.id ? "✓ Selected" : "Select"}
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400">Add keys in env</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {!isMock(selectedGw, gateways) && (
              <div className="mt-3 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                ✓ Live keys detected for <b className="capitalize">{selectedGw}</b> — real-money checkout enabled.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        {visiblePlans.map((p) => {
          const isCurrent = myPlan?.plan === p.code;
          const price = (p.prices?.[currency] || 0) / 100;
          return (
            <Card key={p.code} className={`relative ${isCurrent ? "border-cyan-500 border-2" : ""}`} data-testid={`plan-card-${p.code}`}>
              {p.highlight && <div className="absolute -top-2 right-3 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-1"><Star className="w-3 h-3" /> Popular</div>}
              <CardHeader>
                <CardTitle className="text-base font-[Outfit] flex items-center justify-between">
                  {p.label}
                  {isCurrent && <Badge className="bg-cyan-600">Current</Badge>}
                </CardTitle>
                {p.description && <p className="text-[11px] text-slate-500 mt-1">{p.description}</p>}
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-3xl font-bold">{currency === "inr" ? "₹" : "$"}{price.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 capitalize">/ {p.billing_cycle.replace("_", " ")}</div>
                </div>
                <div className="text-xs text-slate-600">Seats: <b>{p.seats}</b></div>
                <ul className="text-[11px] text-slate-600 space-y-1 max-h-36 overflow-auto">
                  {p.features.slice(0, 10).map((f) => <li key={f} className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500 flex-none" />{f}</li>)}
                  {p.features.length > 10 && <li className="text-slate-400">+{p.features.length - 10} more</li>}
                </ul>
                {isAdmin && p.billing_cycle !== "trial" && !isCurrent && (
                  <Button className="w-full" size="sm" disabled={!!checkoutBusy} onClick={() => checkout(p.code)} data-testid={`subscribe-${p.code}`}>
                    {checkoutBusy === p.code ? "Processing…" : `Subscribe via ${selectedGw}`}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isAdmin && (
        <Card>
          <CardHeader><CardTitle className="text-base font-[Outfit]">Your invoices</CardTitle></CardHeader>
          <CardContent>
            {invoices.length === 0 ? <p className="text-sm text-slate-400">No invoices yet.</p> : (
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Plan</th><th className="text-left p-2">Cycle</th><th className="text-left p-2">Amount</th><th className="text-left p-2">Gateway</th><th className="text-left p-2">Status</th><th className="text-left p-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-t" data-testid={`invoice-${inv.id}`}>
                        <td className="p-2 text-slate-400">{new Date(inv.created_at).toLocaleDateString()}</td>
                        <td className="p-2">{inv.plan_name || inv.plan_code || inv.plan_id}</td>
                        <td className="p-2 capitalize">{(inv.billing_cycle || "").replace("_", " ")}</td>
                        <td className="p-2 font-mono">{(inv.amount / 100).toLocaleString()} {inv.currency?.toUpperCase()}</td>
                        <td className="p-2 capitalize flex items-center gap-1">{gwIcon[inv.gateway]} {inv.gateway}{inv.is_mock && <Badge variant="outline" className="text-[9px] ml-1">Mock</Badge>}</td>
                        <td className="p-2"><Badge variant={inv.status === "paid" ? "default" : inv.status === "awaiting_transfer" ? "outline" : "secondary"} className={inv.status === "paid" ? "bg-emerald-600" : ""}>{inv.status}</Badge></td>
                        <td className="p-2">
                          {inv.status !== "paid" && inv.gateway === "manual" && (
                            <Button size="sm" variant="outline" onClick={() => markPaid(inv.id)} data-testid={`mark-paid-${inv.id}`}>Mark paid</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
